import { Link, usePathname, useRouter } from 'expo-router';
import { ChevronRight, Menu, X } from 'lucide-react-native';
import { useState, type PropsWithChildren, type RefObject } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useAuth } from '@/src/features/auth/use-auth';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import { BrandLogo } from './BrandLogo';

type PublicPageShellProps = PropsWithChildren<{
  maxWidth?: number;
  scrollRef?: RefObject<ScrollView | null>;
  showFooterAddress?: boolean;
  subtitle?: string;
  title?: string;
}>;

type PublicNavLinkProps = {
  href:
    | '/changelog'
    | '/data-deletion'
    | '/'
    | '/home'
    | '/install'
    | '/investors'
    | '/mission'
    | '/privacy'
    | '/refund-policy'
    | '/sign-in'
    | '/sign-up'
    | '/terms';
  label: string;
  fullWidth?: boolean;
  variant?: 'link' | 'primary' | 'secondary';
};

const primaryLinks = [
  { href: '/' as const, label: 'Home' },
  { href: '/mission' as const, label: 'Mission' },
  { href: '/investors' as const, label: 'Investors' },
  { href: '/changelog' as const, label: 'Changelog' },
  { href: '/install' as const, label: 'Install' },
];

function PublicDesktopNavLink({
  href,
  label,
}: Pick<PublicNavLinkProps, 'href' | 'label'>) {
  const pathname = usePathname();
  const active = pathname === href;

  return (
    <Link href={href} asChild>
      <Pressable
        accessibilityRole="link"
        style={({ pressed }) => [
          styles.desktopNavItem,
          pressed ? styles.navItemPressed : null,
        ]}
      >
        <Text
          style={[
            styles.desktopNavText,
            active ? styles.desktopNavTextActive : null,
          ]}
        >
          {label}
        </Text>
        <View
          style={[
            styles.desktopNavUnderline,
            active ? styles.desktopNavUnderlineActive : null,
          ]}
        />
      </Pressable>
    </Link>
  );
}

function PublicMobilePanelLink({
  href,
  label,
  onNavigate,
  variant = 'link',
}: Pick<PublicNavLinkProps, 'href' | 'label' | 'variant'> & {
  onNavigate: () => void;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const active = pathname === href;
  const isPrimary = variant === 'primary';
  const isSecondary = variant === 'secondary';
  const isAction = isPrimary || isSecondary;

  return (
    <Pressable
      accessibilityRole="link"
      onPress={() => {
        onNavigate();
        router.push(href);
      }}
      style={({ pressed }) =>
        [
          styles.mobilePanelLink,
          active && !isPrimary ? styles.mobilePanelLinkActive : null,
          isPrimary ? styles.mobilePanelLinkPrimary : null,
          isSecondary ? styles.mobilePanelLinkSecondary : null,
          pressed ? styles.navItemPressed : null,
        ].filter(Boolean)
      }
    >
      {!isAction ? (
        <View
          style={[
            styles.mobilePanelLinkAccent,
            active ? styles.mobilePanelLinkAccentActive : null,
          ]}
        />
      ) : null}
      <Text
        style={[
          styles.mobilePanelLinkText,
          !isAction ? styles.mobilePanelLinkTextNav : null,
          active && !isPrimary ? styles.mobilePanelLinkTextActive : null,
          isPrimary ? styles.mobilePanelLinkTextPrimary : null,
          isSecondary ? styles.mobilePanelLinkTextSecondary : null,
        ]}
        numberOfLines={1}
      >
        {label}
      </Text>
      {!isAction ? (
        <ChevronRight
          color={active ? colors.primary : colors.mutedText}
          size={18}
          strokeWidth={2.4}
        />
      ) : null}
    </Pressable>
  );
}

function PublicHeaderActionLink({
  href,
  label,
}: Pick<PublicNavLinkProps, 'href' | 'label'>) {
  const router = useRouter();

  return (
    <Pressable
      accessibilityRole="link"
      onPress={() => router.push(href)}
      style={({ pressed }) =>
        pressed ? styles.headerActionButtonPressed : styles.headerActionButton
      }
    >
      <Text style={styles.headerActionText}>{label}</Text>
    </Pressable>
  );
}

function PublicFooterLink({
  href,
  label,
}: Pick<PublicNavLinkProps, 'href' | 'label'>) {
  return (
    <Link href={href} asChild>
      <Pressable accessibilityRole="link" style={styles.footerLinkPressable}>
        <Text style={styles.footerLinkText}>{label}</Text>
      </Pressable>
    </Link>
  );
}

function PublicFooter({
  compact = false,
  showAddress = true,
  testID,
}: {
  compact?: boolean;
  showAddress?: boolean;
  testID?: string;
}) {
  const footerLinks = [
    { href: '/' as const, label: 'Home' },
    { href: '/mission' as const, label: 'Mission' },
    { href: '/investors' as const, label: 'Investors' },
    { href: '/changelog' as const, label: 'Changelog' },
    { href: '/install' as const, label: 'Install' },
    { href: '/terms' as const, label: 'Terms' },
    { href: '/privacy' as const, label: 'Privacy' },
    { href: '/refund-policy' as const, label: 'Refund Policy' },
    { href: '/data-deletion' as const, label: 'Data deletion' },
  ];

  return (
    <View
      style={[styles.footer, compact ? styles.footerCompact : null]}
      testID={testID}
    >
      <View style={styles.footerLinks}>
        {footerLinks.map((link, index) => (
          <View key={link.href} style={styles.footerLinkGroup}>
            {index > 0 ? <Text style={styles.footerSeparator}>|</Text> : null}
            <PublicFooterLink href={link.href} label={link.label} />
          </View>
        ))}
      </View>
      {showAddress ? (
        <Text
          style={[styles.footerText, compact ? styles.footerTextCompact : null]}
        >
          Frienzzi Inc | 2810 N Church St PMB 314550, Wilmington, Delaware
          19802-4447 US
        </Text>
      ) : null}
      <Link href="mailto:support@frienzzi.com" style={styles.footerMailLink}>
        support@frienzzi.com
      </Link>
    </View>
  );
}

export function PublicPageShell({
  children,
  maxWidth = 640,
  scrollRef,
  showFooterAddress = true,
  subtitle,
  title,
}: PublicPageShellProps) {
  const { user } = useAuth();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobileNav = width < 900;
  const isNarrow = width < 420;
  const mobileGutter = isNarrow ? spacing.sm : spacing.md;
  const contentWidth = isMobileNav
    ? Math.max(0, width - mobileGutter * 2)
    : undefined;
  const brandHref = user ? '/home' : '/';
  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.headerWrap} testID="public-page-header">
        <View style={[styles.header, isMobileNav ? styles.headerMobile : null]}>
          <View
            style={[
              styles.headerTopRow,
              isMobileNav ? styles.headerTopRowMobile : null,
            ]}
          >
            <Pressable
              accessibilityRole="link"
              onPress={() => router.push(brandHref)}
              style={
                isMobileNav ? styles.mobileBrandLink : styles.desktopBrandLink
              }
            >
              <BrandLogo size={isMobileNav ? 'sm' : 'sm'} variant="icon" />
              <Text
                numberOfLines={1}
                style={[
                  styles.brandWordmark,
                  isMobileNav ? styles.brandWordmarkMobile : null,
                ]}
              >
                frienzzi
              </Text>
            </Pressable>
            {isMobileNav ? (
              <Pressable
                accessibilityLabel={
                  mobileMenuOpen
                    ? 'Close public navigation'
                    : 'Open public navigation'
                }
                accessibilityRole="button"
                onPress={() => setMobileMenuOpen((open) => !open)}
                style={({ pressed }) => [
                  styles.mobileMenuButton,
                  pressed ? styles.navItemPressed : null,
                ]}
              >
                {mobileMenuOpen ? (
                  <X color={colors.text} size={22} strokeWidth={2.5} />
                ) : (
                  <Menu color={colors.text} size={22} strokeWidth={2.5} />
                )}
              </Pressable>
            ) : (
              <>
                <View style={styles.desktopCenterSlot}>
                  <View style={styles.primaryNav}>
                    {primaryLinks.map((link, index) => (
                      <View key={link.href} style={styles.desktopNavGroup}>
                        {index > 0 ? (
                          <View
                            accessibilityElementsHidden
                            importantForAccessibility="no"
                            style={styles.desktopNavDivider}
                          />
                        ) : null}
                        <PublicDesktopNavLink
                          href={link.href}
                          label={link.label}
                        />
                      </View>
                    ))}
                  </View>
                </View>
                <View style={styles.desktopRightSlot}>
                  <PublicHeaderActionLink
                    href={user ? '/home' : '/sign-in'}
                    label={user ? 'App' : 'Sign in'}
                  />
                </View>
              </>
            )}
          </View>
          {isMobileNav && mobileMenuOpen ? (
            <View style={styles.mobilePanel}>
              <View style={styles.mobilePanelSection}>
                {primaryLinks.map((link) => (
                  <PublicMobilePanelLink
                    key={link.href}
                    href={link.href}
                    label={link.label}
                    onNavigate={closeMobileMenu}
                  />
                ))}
              </View>
              <View style={styles.mobilePanelDivider} />
              {user ? (
                <PublicMobilePanelLink
                  href="/home"
                  label="App"
                  onNavigate={closeMobileMenu}
                  variant="primary"
                />
              ) : (
                <View style={styles.mobilePanelActions}>
                  <PublicMobilePanelLink
                    href="/sign-in"
                    label="Sign in"
                    onNavigate={closeMobileMenu}
                    variant="primary"
                  />
                  <PublicMobilePanelLink
                    href="/sign-up"
                    label="Create account"
                    onNavigate={closeMobileMenu}
                    variant="secondary"
                  />
                </View>
              )}
            </View>
          ) : null}
        </View>
      </View>
      <ScrollView
        ref={scrollRef}
        keyboardShouldPersistTaps="handled"
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.pageBody}>
          <View
            style={[
              styles.content,
              isMobileNav ? styles.contentMobile : null,
              isNarrow ? styles.contentNarrow : null,
              isMobileNav
                ? { maxWidth: contentWidth, width: contentWidth }
                : { maxWidth },
            ]}
          >
            {title ? (
              <View style={styles.titleBlock}>
                <Text
                  style={[
                    styles.title,
                    isMobileNav ? styles.titleMobile : null,
                  ]}
                >
                  {title}
                </Text>
                {subtitle ? (
                  <Text
                    style={[
                      styles.subtitle,
                      isMobileNav ? styles.subtitleMobile : null,
                    ]}
                  >
                    {subtitle}
                  </Text>
                ) : null}
              </View>
            ) : null}
            {children}
          </View>
          <PublicFooter
            compact={isMobileNav}
            showAddress={showFooterAddress}
            testID="public-page-footer"
          />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  content: {
    alignSelf: 'center',
    gap: spacing.xl,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xxl,
    width: '100%',
  },
  contentMobile: {
    alignSelf: 'stretch',
    gap: spacing.lg,
    marginHorizontal: spacing.md,
    paddingHorizontal: 0,
    paddingVertical: spacing.lg,
    width: 'auto',
  },
  contentNarrow: {
    marginHorizontal: spacing.sm,
  },
  desktopNavItem: {
    alignItems: 'center',
    minHeight: 52,
    justifyContent: 'center',
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.sm,
  },
  desktopNavDivider: {
    backgroundColor: colors.border,
    height: 22,
    width: StyleSheet.hairlineWidth,
  },
  desktopNavGroup: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  desktopNavText: {
    color: colors.secondary,
    fontSize: 16,
    fontWeight: '900',
    lineHeight: 22,
  },
  desktopNavTextActive: {
    color: colors.primary,
  },
  desktopNavUnderline: {
    backgroundColor: 'transparent',
    borderRadius: 999,
    height: 3,
    marginTop: spacing.sm,
    width: '100%',
  },
  desktopNavUnderlineActive: {
    backgroundColor: colors.primary,
  },
  footer: {
    alignItems: 'center',
    borderTopColor: colors.border,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: spacing.md,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  footerCompact: {
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.lg,
  },
  footerLinkGroup: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  footerLinkPressable: {
    paddingHorizontal: 2,
    paddingVertical: spacing.xs,
  },
  footerLinks: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    justifyContent: 'center',
  },
  footerLinkText: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '900',
  },
  footerMailLink: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: '800',
  },
  footerSeparator: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '800',
    opacity: 0.75,
  },
  footerText: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
    maxWidth: 680,
    textAlign: 'center',
  },
  footerTextCompact: {
    fontSize: 12,
    lineHeight: 17,
  },
  header: {
    alignItems: 'stretch',
    alignSelf: 'center',
    gap: spacing.sm,
    maxWidth: 1180,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    width: '100%',
  },
  headerMobile: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  headerTopRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xl,
    justifyContent: 'center',
    minHeight: 70,
    width: '100%',
  },
  headerTopRowMobile: {
    gap: spacing.md,
    minHeight: 56,
  },
  headerWrap: {
    backgroundColor: colors.surface,
    borderBottomColor: colors.border,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  brandWordmark: {
    color: colors.text,
    fontSize: 25,
    fontWeight: '900',
    lineHeight: 30,
  },
  brandWordmarkMobile: {
    fontSize: 23,
    lineHeight: 28,
  },
  desktopBrandLink: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 0,
    gap: spacing.sm,
    minWidth: 0,
    paddingRight: spacing.md,
    paddingVertical: spacing.xs,
    width: 260,
  },
  headerActionButton: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 999,
    justifyContent: 'center',
    minHeight: 46,
    minWidth: 112,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.sm,
  },
  headerActionButtonPressed: {
    backgroundColor: colors.primaryPressed,
    alignItems: 'center',
    borderRadius: 999,
    justifyContent: 'center',
    minHeight: 46,
    minWidth: 112,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.sm,
  },
  headerActionText: {
    color: colors.surface,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 20,
  },
  mobileMenuButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  mobileBrandLink: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    minWidth: 0,
    paddingRight: spacing.md,
    paddingVertical: spacing.xs,
  },
  mobilePanel: {
    backgroundColor: colors.background,
    borderColor: colors.border,
    borderRadius: 18,
    borderWidth: 1,
    gap: spacing.md,
    marginBottom: spacing.xs,
    padding: spacing.md,
  },
  mobilePanelActions: {
    gap: spacing.sm,
  },
  mobilePanelDivider: {
    backgroundColor: colors.border,
    height: StyleSheet.hairlineWidth,
  },
  mobilePanelLink: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 14,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
    minHeight: 52,
    paddingHorizontal: spacing.md,
  },
  mobilePanelLinkAccent: {
    backgroundColor: 'transparent',
    borderRadius: 999,
    height: 28,
    width: 4,
  },
  mobilePanelLinkAccentActive: {
    backgroundColor: colors.primary,
  },
  mobilePanelLinkActive: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.primary,
  },
  mobilePanelLinkPrimary: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    justifyContent: 'center',
  },
  mobilePanelLinkSecondary: {
    backgroundColor: colors.surface,
    borderColor: colors.secondary,
    justifyContent: 'center',
  },
  mobilePanelLinkText: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '900',
  },
  mobilePanelLinkTextActive: {
    color: colors.primary,
  },
  mobilePanelLinkTextNav: {
    flex: 1,
  },
  mobilePanelLinkTextPrimary: {
    color: colors.surface,
    textAlign: 'center',
  },
  mobilePanelLinkTextSecondary: {
    color: colors.secondary,
    textAlign: 'center',
  },
  mobilePanelSection: {
    gap: spacing.sm,
  },
  navItemPressed: {
    opacity: 0.75,
  },
  pageBody: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  primaryNav: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'center',
    maxWidth: 620,
    minWidth: 220,
  },
  desktopCenterSlot: {
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  desktopRightSlot: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    width: 260,
  },
  primaryNavItem: {
    backgroundColor: colors.primary,
    minWidth: 92,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  primaryNavText: {
    color: colors.surface,
  },
  safeArea: {
    backgroundColor: colors.background,
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    width: '100%',
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  secondaryNavItem: {
    backgroundColor: colors.surface,
    borderColor: colors.secondary,
    borderWidth: 2,
    minWidth: 92,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  secondaryNavText: {
    color: colors.secondary,
  },
  subtitle: {
    color: colors.mutedText,
    fontSize: 16,
    lineHeight: 22,
  },
  subtitleMobile: {
    fontSize: 15,
    lineHeight: 21,
  },
  title: {
    color: colors.text,
    fontSize: 32,
    fontWeight: '800',
  },
  titleMobile: {
    fontSize: 27,
    lineHeight: 33,
  },
  titleBlock: {
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
});
